import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { GoogleMap, Polygon as GooglePolygon, Marker } from '@react-google-maps/api'
import * as turf from '@turf/turf'
import { kml } from '@tmcw/togeojson'
import { GridMalha, GridPolygon, MalhaGrid } from './components/gerarMalha'
import Configuracoes from './components/config'
import { CorrecaoSobreposicao } from './components/config/abaMapa'
import { calcularCentroTurf, criarPoligono, baixarGridKML, gerarArquivoShp } from './meta'
import { calculoAreaCoord } from '../../../services/Afins'
import { errorAlert, successAlert, warningAlert } from '../../../utils/alert'
import { Render } from '../../../Components/CustomUi/render'
import Carregando from '../../../Components/CustomUi/loading'

// Casas decimais usadas para considerar dois vértices na "mesma coordenada" (~1cm)
const PRECISAO_COORDENADA = 7

// Um polígono válido precisa de no mínimo 3 vértices distintos
const MINIMO_VERTICES_POLIGONO = 3

// Acima desta variação de área a correção é considerada arriscada e não é aplicada
const LIMITE_VARIACAO_AREA = 5

// Teto de passadas da correção — trava de segurança, o loop para sozinho ao zerar
const MAX_PASSOS_CORRECAO = 10

type Coordenada = { lat: number; lng: number }

/** Chave de comparação de coordenada — arredonda para evitar ruído de ponto flutuante */
const chaveDeCoordenada = (lat: number, lng: number) =>
  `${lat.toFixed(PRECISAO_COORDENADA)}_${lng.toFixed(PRECISAO_COORDENADA)}`

/**
 * No fluxo de KML o anel normalmente vem fechado (último ponto repete o primeiro),
 * ao contrário do desenho manual em Leaflet. Tratamos os dois casos.
 */
const anelFechado = (coords: Coordenada[]) => {
  if (coords.length < 2) return false
  const primeiro = coords[0]
  const ultimo = coords[coords.length - 1]
  return chaveDeCoordenada(primeiro.lat, primeiro.lng) === chaveDeCoordenada(ultimo.lat, ultimo.lng)
}

/** Conta vértices desconsiderando o ponto que apenas fecha o anel */
const contarVerticesReais = (coords: Coordenada[]) => {
  if (coords.length < 2) return coords.length
  return anelFechado(coords) ? coords.length - 1 : coords.length
}

/** Quantos vértices do anel estão exatamente nas mesmas coordenadas */
const contarDuplicadosDoAnel = (coords: Coordenada[]) => {
  const lista = anelFechado(coords) ? coords.slice(0, -1) : coords
  if (lista.length <= MINIMO_VERTICES_POLIGONO) return 0

  const vistos = new Set<string>()
  let duplicados = 0

  lista.forEach(({ lat, lng }) => {
    const chave = chaveDeCoordenada(lat, lng)
    if (vistos.has(chave)) duplicados += 1
    else vistos.add(chave)
  })

  return duplicados
}

/** Remove vértices que estão exatamente nas mesmas coordenadas */
const removerDuplicadosDoAnel = (coords: Coordenada[]) => {
  const fechado = anelFechado(coords)
  const lista = fechado ? coords.slice(0, -1) : coords

  const vistos = new Set<string>()
  const unicos: Coordenada[] = []

  lista.forEach(({ lat, lng }) => {
    const chave = chaveDeCoordenada(lat, lng)
    if (!vistos.has(chave)) {
      vistos.add(chave)
      unicos.push({ lat, lng })
    }
  })

  const removidos = lista.length - unicos.length
  if (unicos.length < MINIMO_VERTICES_POLIGONO) return { coords, removidos: 0 }

  return {
    coords: fechado ? [...unicos, { ...unicos[0] }] : unicos,
    removidos,
  }
}

/** Converte o anel em um polígono turf válido para medição */
const paraPoligonoTurf = (coords: Coordenada[]) => {
  const anel = coords.map(({ lat, lng }) => [lng, lat])
  if (!anelFechado(coords)) anel.push([coords[0].lng, coords[0].lat])
  return turf.polygon([anel])
}

/** Quantidade de auto-interseções (vértices sobrepostos) do contorno */
const contarKinks = (coords: Coordenada[]) => {
  try {
    if (coords.length < MINIMO_VERTICES_POLIGONO) return 0
    return turf.kinks(paraPoligonoTurf(coords)).features.length
  } catch {
    return 0
  }
}

/** Área do contorno em hectares */
const areaEmHectares = (coords: Coordenada[]) => {
  try {
    if (coords.length < MINIMO_VERTICES_POLIGONO) return 0
    return turf.area(paraPoligonoTurf(coords)) / 10000
  } catch {
    return 0
  }
}

/**
 * Corrige o contorno com o mínimo de alteração possível.
 *
 * Estratégia em cascata, do menos ao mais invasivo:
 *   1. remover vértices com coordenadas iguais — é a causa mais comum, já que o
 *      contorno é registrado por trator e uma máquina parada grava a mesma
 *      coordenada várias vezes. Não move nenhum vértice e não altera a área;
 *   2. turf.unkinkPolygon, só se ainda sobrar sobreposição — divide o anel no
 *      ponto de cruzamento e mantém a maior parte. Preserva as coordenadas
 *      exatas (não desloca vértices nem infla a contagem).
 *
 * A função é pura: serve para a prévia e para a aplicação.
 */
const corrigirSobreposicoes = (coords: Coordenada[]): CorrecaoSobreposicao => {
  const kinksAntes = contarKinks(coords)
  const areaAntes = areaEmHectares(coords)
  const duplicadosAntes = contarDuplicadosDoAnel(coords)

  const semCorrecao: CorrecaoSobreposicao = {
    coords,
    resolvido: kinksAntes === 0,
    kinksAntes,
    kinksDepois: kinksAntes,
    duplicadosAntes,
    duplicadosRemovidos: 0,
    formatoAlterado: false,
    areaAntes,
    areaDepois: areaAntes,
    variacaoArea: 0,
    descartado: 0,
    arriscado: false,
  }

  // Nada a fazer: sem sobreposição e sem vértices repetidos
  if (kinksAntes === 0 && duplicadosAntes === 0) return semCorrecao
  if (coords.length <= MINIMO_VERTICES_POLIGONO) return semCorrecao

  const montarResultado = (
    bruto: Coordenada[],
    descartado: number,
    duplicadosRemovidos: number,
    formatoAlterado: boolean
  ): CorrecaoSobreposicao => {
    const areaDepois = areaEmHectares(bruto)
    const variacaoArea = areaAntes > 0 ? (Math.abs(areaDepois - areaAntes) / areaAntes) * 100 : 0
    const kinksDepois = contarKinks(bruto)

    return {
      coords: bruto,
      resolvido: kinksDepois === 0,
      kinksAntes,
      kinksDepois,
      duplicadosAntes,
      duplicadosRemovidos,
      formatoAlterado,
      areaAntes,
      areaDepois,
      variacaoArea,
      descartado,
      arriscado: variacaoArea > LIMITE_VARIACAO_AREA || descartado > LIMITE_VARIACAO_AREA,
    }
  }

  // 1) Sempre limpa os vértices repetidos primeiro — passo mais barato e sem risco
  const semDuplicados = removerDuplicadosDoAnel(coords)
  let atual = semDuplicados.removidos > 0 ? semDuplicados.coords : coords
  const duplicadosRemovidos = semDuplicados.removidos

  // Resolveu só com isso: é o caso mais comum (trator parado repetindo coordenada).
  // O formato do contorno não muda, então não há o que revisar.
  if (contarKinks(atual) === 0) {
    if (duplicadosRemovidos === 0) return semCorrecao
    return montarResultado(atual, 0, duplicadosRemovidos, false)
  }

  /**
   * 2) Divide no ponto de cruzamento e mantém a maior parte.
   *
   * Em loop: um contorno com várias sobreposições pode não ser resolvido em uma
   * única passada, e parar no meio deixaria o alerta aceso mesmo "após a correção".
   * Só seguimos enquanto o número de kinks realmente diminui.
   */
  let descartadoAcumulado = 0
  let rodouUnkink = false

  for (let passo = 0; passo < MAX_PASSOS_CORRECAO; passo++) {
    const kinksAtuais = contarKinks(atual)
    if (kinksAtuais === 0) break

    try {
      const partes = turf.unkinkPolygon(paraPoligonoTurf(atual))
      if (!partes?.features?.length) break

      const areas = partes.features.map((feature) => turf.area(feature))
      const areaTotal = areas.reduce((soma, valor) => soma + valor, 0)
      const indiceMaior = areas.indexOf(Math.max(...areas))

      const maior = partes.features[indiceMaior]
      const resultado = maior.geometry.coordinates[0].map(([lng, lat]) => ({ lat, lng }))
      if (contarVerticesReais(resultado) < MINIMO_VERTICES_POLIGONO) break

      // Sem progresso: aborta para não girar em falso nem degradar o contorno
      if (contarKinks(resultado) >= kinksAtuais) break

      if (areaTotal > 0) {
        descartadoAcumulado += ((areaTotal - areas[indiceMaior]) / areaTotal) * 100
      }
      atual = resultado
      rodouUnkink = true
    } catch {
      break
    }
  }

  // Nem os duplicados nem o unkink alteraram o contorno
  if (atual === coords) return semCorrecao

  return montarResultado(atual, descartadoAcumulado, duplicadosRemovidos, rodouUnkink)
}

const GridMap = () => {
  const mapRef = useRef<google.maps.Map | null>(null)
  const polygonRef = useRef<google.maps.Polygon | null>(null)

  const [loadingKmlAdjustment, setLoadingKmlAdjustment] = useState(false)

  const [kmlComCruzamento, setKmlComCruzamento] = useState(null)
  const [disable, setDisable] = useState(true)
  const [validoFile, setValidoFile] = useState(false)
  const [area, setArea] = useState([])
  const [editandoKml, setEditandoKml] = useState<boolean>(false)
  const [pontosKinks, setPontosKinks] = useState<{ lat: number; lng: number }[]>([])
  const [openReducaoTrilhas, setOpenReducaoTrilhas] = useState<boolean>(false)
  const [distanciaTrilhas, setDistanciaTrilhas] = useState<number>(1)
  const [openReducaoRetas, setOpenReducaoRetas] = useState<boolean>(false)
  const [toleranciaRetas, setToleranciaRetas] = useState<number>(1)
  const [openCorrigirKinks, setOpenCorrigirKinks] = useState<boolean>(false)
  /**
   * Contador incrementado a cada edição do path do polígono. O path é mutável e
   * vive fora do React, então precisamos de um gatilho explícito para recalcular
   * as prévias depois de arrastar ou remover um vértice.
   */
  const [contornoVersao, setContornoVersao] = useState<number>(0)

  //GRID
  const [rotation, setRotation] = useState<number>(0)
  const [movendo, setMovendo] = useState<boolean>(false)
  const [areaHa, setAreaHa] = useState<number>(0)
  const [gridLevel, setGridLevel] = useState<string>('')
  const [position, setPosition] = useState<{ x: 0; y: 0 }>({ x: 0, y: 0 })
  const [grid, setGrid] = useState<GridMalha[]>([])
  const [gridOriginal, setGridOriginal] = useState<GridMalha[]>([])
  const [gridOriginalRotacao, setGridOriginalRotacao] = useState<GridMalha[]>([])
  const [malhaPolygon, setMalhaPolygon] = useState<GridPolygon[]>([])
  const [malhaPolygonOriginal, setMalhaPolygonOriginal] = useState<GridPolygon[]>([])
  const [tamanhoGridOriginal, setTamanhoGridOriginal] = useState<number>(0)
  const [abrirModal, setAbrirModal] = useState<boolean>(false)
  const [pontosSelecionados, setPontosSelecionados] = useState([])
  const [nomeTalhao, setNomeTalhao] = useState<string>('')
  const [clicou, setClicou] = useState<boolean>(false)
  const [mousePosition, setMousePosition] = useState<[number, number] | null>(null)
  const [mapClickEvent, setMapClickEvent] = useState<{ lat: number; lng: number; ts: number } | null>(null)

  //CONFIGURAÇÕES
  const [tab_container_atual, setTab_container_atual] = useState<string>('Mapa')
  const [novoPonto, setNovoPonto] = useState<boolean>(false)
  const [moverPonto, setMoverPonto] = useState<boolean>(false)
  const [removerPonto, setRemoverPonto] = useState<boolean>(false)
  const [selecionarPonto, setSelecionarPonto] = useState<boolean>(false)
  const [abaAtual, setAbaAtual] = useState<number>(0)
  const abasNome = ['Mapa', 'Pontos', 'Ordenacao']
  const [confirmado, setConfirmado] = useState<boolean>(false)
  const [arquivoSelecionado, setArquivoSelecionado] = useState<string>('')
  const [rotacaoInput, setRotacaoInput] = useState<string>('')
  const [reset, setReset] = useState(true)
  const [quadrante, setQuadrante] = useState<number>(0)

  const padrao = useMemo(() => [
    [-15.7801, -47.9292],
    [-15.7801, -47.9192],
    [-15.7701, -47.9192],
    [-15.7701, -47.9292],
    [-15.7801, -47.9292],
  ], [])

  const kmlCoordsLatLng = useMemo(() => {
    if (area.length <= 0) return padrao
    return area.map((item) => [item.lat, item.lng])
  }, [area, padrao])

  // ancora retorna [lng, lat] do turf — convertemos para {lat, lng}
  const ancoraRaw = useMemo(() => calcularCentroTurf(kmlCoordsLatLng), [kmlCoordsLatLng])
  const ancora = useMemo(() => {
    if (!ancoraRaw) return null
    return { lat: ancoraRaw[1], lng: ancoraRaw[0] }
  }, [ancoraRaw])

  const polygonKml = useMemo(() => criarPoligono(kmlCoordsLatLng), [kmlCoordsLatLng])

  const aoCarregarArquivo = useCallback((rawData: string, base64Data) => {
    extrairCoordenadas(rawData)
  }, [])

  const converterXmlDeString = async (xmlString: string): Promise<Document> => {
    const parser = new DOMParser()
    const xmlDoc = parser.parseFromString(xmlString, 'application/xml')
    return xmlDoc
  }

  const extrairCoordenadas = async (kmlString: string) => {
    try {
      const domKml = await converterXmlDeString(kmlString)

      const dataGeoJson = kml(domKml)
      setNomeTalhao(dataGeoJson?.features[0]?.properties?.name)
      const talhoes = []

      const dados = dataGeoJson.features
      const filtrado = dados.filter((data) => data.geometry.type === 'Polygon')

      if (filtrado.length > 0) {
        for (let i = 0; i < filtrado.length; i += 1) {
          const fragment = filtrado[i]
          const talhaoPoly = []

          const coordinates = fragment.geometry.coordinates[0]

          for (let ii = 0; ii < coordinates.length; ii += 1) {
            const coord = coordinates[ii]
            talhaoPoly.push({ lat: coord[1], lng: coord[0] })
          }

          talhoes.push(talhaoPoly)
        }

        const areaCoord = talhoes[0].map((item) => ({ lat: item.lat, lng: item.lng }))
        setArea(areaCoord)
        const calcArea = calculoAreaCoord([areaCoord])
        setAreaHa(calcArea)
        if (calcArea <= 0) {
          errorAlert('Arquivo Kml inválido!')
          setValidoFile(false)
          setTimeout(() => { setDisable(true) }, 1000)
        } else {
          setValidoFile(true)
          setTimeout(() => { setDisable(false) }, 1000)
        }
      } else {
        errorAlert('Arquivo kml inválido!')
        setValidoFile(false)
      }
    } catch (error) {
      console.error('Erro ao processar XML:', error)
    }
  }

  const verificarAvancar = useCallback(() => {
    const temValor = grid.some((item) => 'posicao' in item)
    const tamanhoGrid = grid.filter(({ center }) =>
      turf.booleanPointInPolygon(turf.point([center[1], center[0]]), polygonKml)
    )
    if (temValor && abaAtual === 2) {
      return setAbrirModal(true)
    }
    if (tamanhoGridOriginal !== tamanhoGrid.length && abaAtual === 1) {
      return setAbrirModal(true)
    }
    voltar()
  }, [grid, polygonKml, abaAtual, tamanhoGridOriginal])

  const avancar = useCallback(() => {
    if (abaAtual < abasNome.length - 1) {
      setAbaAtual((prev) => prev + 1)
    }
  }, [abaAtual, abasNome.length])

  const voltar = useCallback(() => {
    if (abaAtual > 0) {
      setAbaAtual((prev) => prev - 1)
    }
  }, [abaAtual])

  const resetarGrid = useCallback(() => {
    setReset(false)
    setGrid(gridOriginalRotacao)
    setPontosSelecionados([])
    setMousePosition(null)
  }, [gridOriginalRotacao])

  // Baixar arquivo (KML ou SHP) sem fechar/limpar
  const baixarMalha = useCallback((tipo: string) => {
    if (tipo === 'Kml') {
      baixarGridKML(grid, nomeTalhao)
    } else {
      gerarArquivoShp(grid, nomeTalhao)
    }
  }, [grid, nomeTalhao])

  // Salvar malha — no módulo, apenas reseta o estado (não há pai para notificar)
  const salvarMalha = useCallback(() => {
    // Baixa automaticamente o arquivo ao salvar
    if (arquivoSelecionado === 'Kml') {
      baixarGridKML(grid, nomeTalhao)
    } else if (arquivoSelecionado) {
      gerarArquivoShp(grid, nomeTalhao)
    }

    // Resetar estado
    setArquivoSelecionado('')
    setAbaAtual(0)
    setTab_container_atual('Mapa')
    setGrid([])
    setPontosSelecionados([])
  }, [grid, nomeTalhao, arquivoSelecionado])

  useEffect(() => {
    if (confirmado) {
      if (abaAtual === 2) {
        voltar()
        setAbrirModal(false)
        setSelecionarPonto(false)
        resetarGrid()
        setArquivoSelecionado('')
        setTimeout(() => { setConfirmado(false) }, 500)
      }
      if (abaAtual === 1) {
        voltar()
        setAbrirModal(false)
        setGrid(gridOriginal)
        setRotation(0)
        setRotacaoInput('0')
        setNovoPonto(false)
        setMoverPonto(false)
        setRemoverPonto(false)
        setTimeout(() => { setConfirmado(false) }, 500)
      }
    }
  }, [confirmado])

  /**
   * Ao entrar/sair do modo de edição o polígono é remontado (muda `editable`),
   * então o path só existe no tick seguinte. Este bump garante que as prévias
   * recalculem já com o path disponível, em vez de esperar um re-render alheio.
   */
  useEffect(() => {
    setContornoVersao((prev) => prev + 1)
  }, [editandoKml, area])

  useEffect(() => {
    if (area.length > 0) {
      const timer = setTimeout(() => {
        const kinks = turf.kinks(polygonKml)

        if (kinks.features.length > 0) {
          const pontos = kinks.features.map((f) => ({
            lat: f.geometry.coordinates[1],
            lng: f.geometry.coordinates[0],
          }))
          warningAlert(`O contorno possui ${pontos.length} vértice(s) sobreposto(s), habilite o editar contorno para ajustar`)
          setPontosKinks(pontos)
          setKmlComCruzamento(true)
          setLoadingKmlAdjustment(false)
          return
        }

        setPontosKinks([])
        setEditandoKml(false)
        setGrid([])
        setGridOriginal([])
        setGridOriginalRotacao([])
        setTamanhoGridOriginal(null)
        setClicou(false)
        setRotation(0)
        setKmlComCruzamento(false)
        setLoadingKmlAdjustment(false)
      }, 100)

      return () => clearTimeout(timer)
    }
  }, [area, polygonKml])

  // Salvar contorno editado manualmente
  const salvarContorno = useCallback(() => {
    if (!polygonRef.current) return
    setLoadingKmlAdjustment(true)
    requestAnimationFrame(() => {
      const path = polygonRef.current?.getPath()
      if (!path) return
      const novaArea: { lat: number; lng: number }[] = []
      for (let i = 0; i < path.getLength(); i++) {
        const pt = path.getAt(i)
        novaArea.push({ lat: pt.lat(), lng: pt.lng() })
      }
      if (novaArea.length > 0) {
        const first = novaArea[0]
        const last = novaArea[novaArea.length - 1]
        if (first.lat !== last.lat || first.lng !== last.lng) {
          novaArea.push({ ...first })
        }
      }
      setArea(novaArea)
    })
  }, [])

  // Verificar kinks diretamente a partir do path atual do polígono no mapa
  const verificarKinksDoPoligono = useCallback(() => {
    if (!polygonRef.current) return

    const path = polygonRef.current.getPath()
    const coords: [number, number][] = []
    for (let i = 0; i < path.getLength(); i++) {
      const pt = path.getAt(i)
      coords.push([pt.lng(), pt.lat()])
    }

    // Fechar o anel se necessário
    if (coords.length >= 3) {
      const first = coords[0]
      const last = coords[coords.length - 1]
      if (first[0] !== last[0] || first[1] !== last[1]) {
        coords.push([...first])
      }
    }

    if (coords.length < 4) return

    const poly = turf.polygon([coords])
    const kinks = turf.kinks(poly)

    if (kinks.features.length > 0) {
      // Ainda tem cruzamentos — atualizar apenas os markers
      const pontos = kinks.features.map((f) => ({
        lat: f.geometry.coordinates[1],
        lng: f.geometry.coordinates[0],
      }))
      setPontosKinks(pontos)
    } else {
      // Sem cruzamentos — salvar contorno automaticamente e sair do modo edição
      setPontosKinks([])
      setKmlComCruzamento(false)
      setEditandoKml(false)

      // Atualizar area com o path atual
      const novaArea: { lat: number; lng: number }[] = []
      for (let i = 0; i < path.getLength(); i++) {
        const pt = path.getAt(i)
        novaArea.push({ lat: pt.lat(), lng: pt.lng() })
      }
      if (novaArea.length > 0) {
        const first = novaArea[0]
        const last = novaArea[novaArea.length - 1]
        if (first.lat !== last.lat || first.lng !== last.lng) {
          novaArea.push({ ...first })
        }
      }
      setArea(novaArea)
    }
  }, [])

  // Chave de comparação de coordenada — reexporta o helper de módulo
  const chaveDaCoordenada = useCallback(
    (lat: number, lng: number) => chaveDeCoordenada(lat, lng),
    []
  )

  // Lê o contorno atual direto do path do polígono no mapa
  const lerCoordenadasDoPath = useCallback(() => {
    if (!polygonRef.current) return []

    const path = polygonRef.current.getPath()
    const coords: Coordenada[] = []
    for (let i = 0; i < path.getLength(); i++) {
      const pt = path.getAt(i)
      coords.push({ lat: pt.lat(), lng: pt.lng() })
    }
    return coords
  }, [])

  /** Reescreve o path do polígono no mapa com o contorno informado */
  const escreverCoordenadasNoPath = useCallback((coords: Coordenada[]) => {
    if (!polygonRef.current) return
    const path = polygonRef.current.getPath()
    path.clear()
    coords.forEach(({ lat, lng }) => { path.push(new google.maps.LatLng(lat, lng)) })
  }, [])

  /**
   * Correção do contorno atual.
   *
   * Prefere o path do mapa, porque durante a edição ele divirge do estado `area`
   * e a prévia precisa refletir exatamente o que será aplicado. Cai para `area`
   * enquanto o path não existe — o `polygonRef` só é preenchido no `onLoad` do
   * polígono, depois de a API do Google Maps carregar.
   *
   * ATENÇÃO às dependências: o path é um objeto mutável fora do React, então
   * nada aqui reage sozinho a ele. `contornoVersao` cobre as edições manuais e
   * `editandoKml` cobre a entrada no modo de edição (que é quando o botão
   * aparece) — sem ele o valor calculado com o path ainda vazio ficava em cache
   * e o botão só habilitava num re-render alheio, como dar zoom no mapa.
   */
  const correcaoSobreposicoes = useMemo(() => {
    void contornoVersao
    void editandoKml
    const doPath = lerCoordenadasDoPath()
    return corrigirSobreposicoes(doPath.length > 0 ? doPath : area)
  }, [area, contornoVersao, editandoKml, lerCoordenadasDoPath])

  // Há sobreposição ou vértice repetido a tratar
  const temAlgoParaCorrigir =
    correcaoSobreposicoes.kinksAntes > 0 || correcaoSobreposicoes.duplicadosAntes > 0

  const aplicarCorrecaoSobreposicoes = useCallback(() => {
    if (!polygonRef.current) return

    const {
      coords,
      resolvido,
      arriscado,
      variacaoArea,
      duplicadosRemovidos,
      formatoAlterado,
      kinksAntes,
      kinksDepois,
    } = correcaoSobreposicoes

    if (!temAlgoParaCorrigir) {
      warningAlert('Nenhuma sobreposição ou vértice repetido encontrado no contorno.')
      return
    }

    // Correção possível, mas alteraria demais o contorno — decisão fica com o usuário
    if (arriscado) {
      errorAlert(
        `Correção não aplicada: alteraria ${variacaoArea.toFixed(1)}% da área. Ajuste os vértices manualmente.`
      )
      return
    }

    // Nada mudou: nem duplicados removidos, nem o unkink rodou
    if (duplicadosRemovidos === 0 && !formatoAlterado) {
      warningAlert('Não foi possível corrigir automaticamente. Ajuste os vértices manualmente.')
      return
    }

    escreverCoordenadasNoPath(coords)
    setOpenCorrigirKinks(false)
    setContornoVersao((prev) => prev + 1)
    verificarKinksDoPoligono()

    // Detalha o que cada etapa resolveu — duplicados são a causa mais comum
    const partes: string[] = []
    if (duplicadosRemovidos > 0) {
      partes.push(`${duplicadosRemovidos} vértice(s) repetido(s) removido(s)`)
    }
    // Só creditamos a correção de sobreposição quando a etapa 2 realmente rodou:
    // vértices repetidos por si já acusam kink, e removê-los resolve sem ajustar nada
    if (kinksAntes > 0 && formatoAlterado) {
      partes.push(
        resolvido
          ? `${kinksAntes} sobreposição(ões) corrigida(s)`
          : `${kinksAntes - kinksDepois} de ${kinksAntes} sobreposição(ões) corrigida(s)`
      )
    }

    const resumo = partes.join(' e ')

    if (!resolvido) {
      errorAlert(
        `${resumo}. Ainda restam ${kinksDepois} sobreposição(ões) — ajuste os vértices manualmente.`
      )
      return
    }

    /**
     * O aviso de revisar só faz sentido se o formato do contorno mudou, ou seja
     * se a etapa 2 rodou. Remover vértices repetidos não move nenhum vértice.
     */
    successAlert(
      formatoAlterado
        ? `${resumo}. Revise o contorno antes de salvar.`
        : `Correção automática aplicada: ${resumo}.`
    )
  }, [
    correcaoSobreposicoes,
    temAlgoParaCorrigir,
    lerCoordenadasDoPath,
    escreverCoordenadasNoPath,
    verificarKinksDoPoligono,
  ])

  /**
   * Reduz a quantidade de vértices do contorno mantendo apenas os que estão a
   * pelo menos `distanciaMinima` metros do último vértice preservado.
   * Não altera o mapa — serve tanto para a prévia quanto para a aplicação.
   */
  const calcularReducaoDeTrilhas = useCallback(
    (coords: { lat: number; lng: number }[], distanciaMinima: number) => {
      if (coords.length <= MINIMO_VERTICES_POLIGONO) return coords

      // O fechamento do anel é tratado à parte para nunca ser descartado
      const primeiro = coords[0]
      const ultimo = coords[coords.length - 1]
      const fechado =
        chaveDaCoordenada(primeiro.lat, primeiro.lng) === chaveDaCoordenada(ultimo.lat, ultimo.lng)
      const lista = fechado ? coords.slice(0, -1) : coords

      if (lista.length <= MINIMO_VERTICES_POLIGONO) return coords

      const mantidos: { lat: number; lng: number }[] = [lista[0]]

      for (let i = 1; i < lista.length; i++) {
        const atual = lista[i]
        const referencia = mantidos[mantidos.length - 1]

        const distancia = turf.distance(
          turf.point([referencia.lng, referencia.lat]),
          turf.point([atual.lng, atual.lat]),
          { units: 'meters' }
        )

        if (distancia >= distanciaMinima) {
          mantidos.push(atual)
        }
      }

      // Garante que o polígono continue válido mesmo com um intervalo agressivo
      if (mantidos.length < MINIMO_VERTICES_POLIGONO) return coords

      // O último vértice mantido pode ter ficado colado no primeiro ao fechar o anel
      if (mantidos.length > MINIMO_VERTICES_POLIGONO) {
        const inicio = mantidos[0]
        const fim = mantidos[mantidos.length - 1]
        const distanciaFechamento = turf.distance(
          turf.point([inicio.lng, inicio.lat]),
          turf.point([fim.lng, fim.lat]),
          { units: 'meters' }
        )
        if (distanciaFechamento < distanciaMinima) {
          mantidos.pop()
        }
      }

      return fechado ? [...mantidos, { ...mantidos[0] }] : mantidos
    },
    [chaveDaCoordenada]
  )

  /**
   * Prévia da redução exibida no popover.
   *
   * Lê o path do polígono no mapa (e não o estado `area`) porque durante a
   * edição do contorno os dois divergem — assim os números mostrados são
   * exatamente os que serão aplicados.
   */
  const previaReducaoTrilhas = useMemo(() => {
    // `openReducaoTrilhas` e `area` entram como dependências apenas para
    // forçar o recálculo ao abrir o popover e ao recarregar o contorno
    void openReducaoTrilhas
    void area

    const coords = lerCoordenadasDoPath()

    // Desconta o fechamento do anel para não contar o mesmo ponto duas vezes
    const contarReais = (lista: { lat: number; lng: number }[]) => {
      if (lista.length < 2) return lista.length
      const primeiro = lista[0]
      const ultimo = lista[lista.length - 1]
      const fechado =
        chaveDaCoordenada(primeiro.lat, primeiro.lng) === chaveDaCoordenada(ultimo.lat, ultimo.lng)
      return fechado ? lista.length - 1 : lista.length
    }

    const total = contarReais(coords)

    if (total === 0) {
      return { total: 0, aposReducao: 0, removidos: 0, porcentagem: 0 }
    }

    const reduzido = calcularReducaoDeTrilhas(coords, distanciaTrilhas)
    const aposReducao = contarReais(reduzido)
    const removidos = total - aposReducao
    const porcentagem = total > 0 ? (removidos / total) * 100 : 0

    return { total, aposReducao, removidos, porcentagem }
  }, [
    area,
    openReducaoTrilhas,
    distanciaTrilhas,
    lerCoordenadasDoPath,
    calcularReducaoDeTrilhas,
    chaveDaCoordenada,
  ])

  // Aplica a redução de trilhas no contorno
  const aplicarReducaoTrilhas = useCallback(() => {
    if (!polygonRef.current) return

    const coords = lerCoordenadasDoPath()
    if (coords.length <= MINIMO_VERTICES_POLIGONO) {
      warningAlert('Não é possível reduzir: o polígono precisa ter no mínimo 3 vértices.')
      return
    }

    const reduzido = calcularReducaoDeTrilhas(coords, distanciaTrilhas)
    const removidos = coords.length - reduzido.length

    if (removidos <= 0) {
      warningAlert(
        `Nenhum vértice foi removido com o intervalo de ${distanciaTrilhas}m. Tente um valor maior.`
      )
      return
    }

    const path = polygonRef.current.getPath()
    path.clear()
    reduzido.forEach(({ lat, lng }) => { path.push(new google.maps.LatLng(lat, lng)) })

    setOpenReducaoTrilhas(false)
    setContornoVersao((prev) => prev + 1)
    verificarKinksDoPoligono()

    successAlert(`${removidos} vértice(s) removido(s) pela redução de trilhas.`)
  }, [
    lerCoordenadasDoPath,
    calcularReducaoDeTrilhas,
    distanciaTrilhas,
    verificarKinksDoPoligono,
  ])

  /**
   * Simplifica apenas trechos retos do contorno (Douglas-Peucker).
   *
   * Diferente da redução por distância, aqui o critério é o desvio
   * perpendicular: um vértice só é removido se a linha continuar praticamente
   * no mesmo lugar sem ele. Assim uma reta com 50 vértices colapsa para 2,
   * enquanto cantos e curvas são preservados.
   */
  const calcularReducaoDeRetas = useCallback(
    (coords: { lat: number; lng: number }[], toleranciaMetros: number) => {
      if (coords.length <= MINIMO_VERTICES_POLIGONO) return coords

      const primeiro = coords[0]
      const ultimo = coords[coords.length - 1]
      const fechado =
        chaveDaCoordenada(primeiro.lat, primeiro.lng) === chaveDaCoordenada(ultimo.lat, ultimo.lng)

      // turf.simplify espera um anel fechado
      const anel = fechado ? coords : [...coords, { ...primeiro }]
      if (anel.length < 4) return coords

      // A tolerância do turf.simplify é em graus — convertemos a partir dos
      // metros usando a latitude do contorno como referência
      const grausPorMetro = 1 / 111320
      const tolerancia = toleranciaMetros * grausPorMetro

      try {
        /**
         * Simplificamos como LineString, e não como Polygon: turf.simplify
         * lança "invalid polygon" quando o anel cairia abaixo de 4 coordenadas,
         * e não simplifica anéis auto-interseccionados — justamente o caso dos
         * contornos que este módulo precisa corrigir.
         */
        const linha = turf.lineString(anel.map(({ lat, lng }) => [lng, lat]))
        const simplificada = turf.simplify(linha, { tolerance: tolerancia, highQuality: true })

        const resultado = simplificada.geometry.coordinates.map(([lng, lat]) => ({ lat, lng }))

        // Se a simplificação degenerar o polígono, mantém o original
        if (contarVerticesReais(resultado) < MINIMO_VERTICES_POLIGONO) return coords

        // Garante que o anel continue fechado após a simplificação
        const inicio = resultado[0]
        const fim = resultado[resultado.length - 1]
        const aindaFechado = chaveDaCoordenada(inicio.lat, inicio.lng) === chaveDaCoordenada(fim.lat, fim.lng)
        if (!aindaFechado) resultado.push({ ...inicio })

        return fechado ? resultado : resultado.slice(0, -1)
      } catch {
        return coords
      }
    },
    [chaveDaCoordenada, contarVerticesReais]
  )

  // Prévia da simplificação de retas — lê o path do mapa, como a outra prévia
  const previaReducaoRetas = useMemo(() => {
    void openReducaoRetas
    void area

    const coords = lerCoordenadasDoPath()
    const total = contarVerticesReais(coords)

    if (total === 0) {
      return { total: 0, aposReducao: 0, removidos: 0, porcentagem: 0 }
    }

    const reduzido = calcularReducaoDeRetas(coords, toleranciaRetas)
    const aposReducao = contarVerticesReais(reduzido)
    const removidos = total - aposReducao
    const porcentagem = total > 0 ? (removidos / total) * 100 : 0

    return { total, aposReducao, removidos, porcentagem }
  }, [
    area,
    openReducaoRetas,
    toleranciaRetas,
    lerCoordenadasDoPath,
    calcularReducaoDeRetas,
    contarVerticesReais,
  ])

  /**
   * Geometria da prévia desenhada no mapa enquanto o popover está aberto:
   * o contorno resultante e os vértices que serão descartados.
   */
  const previewNoMapa = useMemo(() => {
    const vazio = { contorno: [] as Coordenada[], descartados: [] as Coordenada[] }

    if (!openReducaoRetas && !openReducaoTrilhas && !openCorrigirKinks) return vazio

    void area
    void contornoVersao

    // Cai para `area` enquanto o path do mapa ainda não existe
    const doPath = lerCoordenadasDoPath()
    const coords = doPath.length > 0 ? doPath : area
    if (coords.length <= MINIMO_VERTICES_POLIGONO) return vazio

    let reduzido: Coordenada[]
    if (openCorrigirKinks) reduzido = correcaoSobreposicoes.coords
    else if (openReducaoRetas) reduzido = calcularReducaoDeRetas(coords, toleranciaRetas)
    else reduzido = calcularReducaoDeTrilhas(coords, distanciaTrilhas)

    // Nada a mostrar quando a redução/correção devolveu o contorno intacto
    if (reduzido === coords) return vazio
    if (openCorrigirKinks && !temAlgoParaCorrigir) return vazio

    // Vértices que não sobreviveram à redução
    const mantidos = new Set(reduzido.map(({ lat, lng }) => chaveDaCoordenada(lat, lng)))
    const descartados = coords.filter(
      ({ lat, lng }) => !mantidos.has(chaveDaCoordenada(lat, lng))
    )

    return { contorno: reduzido, descartados }
  }, [
    area,
    contornoVersao,
    openReducaoRetas,
    openReducaoTrilhas,
    openCorrigirKinks,
    toleranciaRetas,
    distanciaTrilhas,
    correcaoSobreposicoes,
    temAlgoParaCorrigir,
    lerCoordenadasDoPath,
    calcularReducaoDeRetas,
    calcularReducaoDeTrilhas,
    chaveDaCoordenada,
  ])

  // Aplica a simplificação de trechos retos no contorno
  const aplicarReducaoRetas = useCallback(() => {
    if (!polygonRef.current) return

    const coords = lerCoordenadasDoPath()
    if (coords.length <= MINIMO_VERTICES_POLIGONO) {
      warningAlert('Não é possível simplificar: o polígono precisa ter no mínimo 3 vértices.')
      return
    }

    const reduzido = calcularReducaoDeRetas(coords, toleranciaRetas)
    const removidos = coords.length - reduzido.length

    if (removidos <= 0) {
      warningAlert(
        `Nenhum trecho reto foi encontrado com a tolerância de ${toleranciaRetas}m. Tente um valor maior.`
      )
      return
    }

    const path = polygonRef.current.getPath()
    path.clear()
    reduzido.forEach(({ lat, lng }) => { path.push(new google.maps.LatLng(lat, lng)) })

    setOpenReducaoRetas(false)
    setContornoVersao((prev) => prev + 1)
    verificarKinksDoPoligono()

    successAlert(`${removidos} vértice(s) removido(s) de trechos retos.`)
  }, [
    lerCoordenadasDoPath,
    calcularReducaoDeRetas,
    toleranciaRetas,
    verificarKinksDoPoligono,
  ])

  // Remover vértice ao clicar com o botão direito
  const removerVertice = useCallback((e: google.maps.PolyMouseEvent) => {
    if (!editandoKml) return
    if (e.vertex == null) return
    if (!polygonRef.current) return

    const path = polygonRef.current.getPath()
    // Mínimo de 3 vértices para manter um polígono válido
    if (path.getLength() <= 3) {
      warningAlert('Não é possível remover: o polígono precisa ter no mínimo 3 vértices.')
      return
    }

    path.removeAt(e.vertex)
    setContornoVersao((prev) => prev + 1)
    verificarKinksDoPoligono()
  }, [editandoKml, verificarKinksDoPoligono])

  // Ao mover um vértice manualmente (arrastar), re-verificar kinks
  const aoEditarPoligono = useCallback(() => {
    if (!editandoKml) return
    setContornoVersao((prev) => prev + 1)
    verificarKinksDoPoligono()
  }, [editandoKml, verificarKinksDoPoligono])

  useEffect(() => {
    abasNome.forEach((item, index) => {
      if (index === abaAtual) {
        setTab_container_atual(item)
      }
    })
    if (abaAtual === 1) {
      setSelecionarPonto(false)
      setPontosSelecionados([])
    }
    if (abaAtual === 2) {
      setSelecionarPonto(true)
      setNovoPonto(false)
      setMoverPonto(false)
      setRemoverPonto(false)
    }
  }, [abaAtual])

  // Ajustar bounds do mapa quando as coordenadas mudam (exceto durante edição de contorno)
  useEffect(() => {
    if (!mapRef.current || area.length === 0) return
    if (editandoKml) return
    const bounds = new google.maps.LatLngBounds()
    area.forEach(({ lat, lng }) => { bounds.extend({ lat, lng }) })
    mapRef.current.fitBounds(bounds)
  }, [area, editandoKml])

  const center = useMemo(() => {
    if (area.length <= 0) return { lat: -14.235, lng: -51.925 }
    if (ancora) return ancora
    return { lat: kmlCoordsLatLng[0][0], lng: kmlCoordsLatLng[0][1] }
  }, [kmlCoordsLatLng, ancora, area])

  // Paths do polígono KML para o Google Maps
  const kmlPolygonPaths = useMemo(() => {
    return kmlCoordsLatLng.map(([lat, lng]) => ({ lat, lng }))
  }, [kmlCoordsLatLng])

  const aoClicarNoMapa = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return
      setMapClickEvent({ lat: e.latLng.lat(), lng: e.latLng.lng(), ts: Date.now() })
    },
    []
  )

  const aoMoverMouseNoMapa = useCallback(
    (e: google.maps.MapMouseEvent) => {
      if (!e.latLng) return
      if (pontosSelecionados.length === 1) {
        setMousePosition([e.latLng.lat(), e.latLng.lng()])
      }
    },
    [pontosSelecionados]
  )

  const aoCarregarMapa = useCallback((map: google.maps.Map) => {
    mapRef.current = map
    if (area.length > 0) {
      const bounds = new google.maps.LatLngBounds()
      area.forEach(({ lat, lng }) => { bounds.extend({ lat, lng }) })
      map.fitBounds(bounds)
    }
  }, [area])

  return (
    <div style={{ display: 'flex', position: 'relative', overflow: 'hidden', height: '100vh' }}>
      <Configuracoes
        disable={disable}
        setDisable={setDisable}
        aoCarregarArquivo={aoCarregarArquivo}
        validoFile={validoFile}
        setValidoFile={setValidoFile}
        rotation={rotation}
        setRotation={setRotation}
        rotacaoInput={rotacaoInput}
        setRotacaoInput={setRotacaoInput}
        movendo={movendo}
        setMovendo={setMovendo}
        novoPonto={novoPonto}
        setNovoPonto={setNovoPonto}
        moverPonto={moverPonto}
        setMoverPonto={setMoverPonto}
        removerPonto={removerPonto}
        setRemoverPonto={setRemoverPonto}
        selecionarPonto={selecionarPonto}
        setSelecionarPonto={setSelecionarPonto}
        gridLevel={gridLevel}
        setGridLevel={setGridLevel}
        position={position}
        setPosition={setPosition}
        tab_container_atual={tab_container_atual}
        setTab_container_atual={setTab_container_atual}
        arquivoSelecionado={arquivoSelecionado}
        setArquivoSelecionado={setArquivoSelecionado}
        setGrid={setGrid}
        setGridOriginal={setGridOriginal}
        setGridOriginalRotacao={setGridOriginalRotacao}
        setMalhaPolygon={setMalhaPolygon}
        setMalhaPolygonOriginal={setMalhaPolygonOriginal}
        setTamanhoGridOriginal={setTamanhoGridOriginal}
        setQuadrante={setQuadrante}
        setClicou={setClicou}
        areaHectares={areaHa}
        polygon={polygonKml}
        kmlComCruzamento={kmlComCruzamento}
        editandoKml={editandoKml}
        setEditandoKml={setEditandoKml}
        grid={grid}
        abaAtual={abaAtual}
        abasNome={abasNome}
        pontosSelecionados={pontosSelecionados}
        area={area}
        areaTamanho={areaHa}
        quadrante={quadrante}
        avancar={avancar}
        voltar={verificarAvancar}
        resetarGrid={resetarGrid}
        nomeTalhao={nomeTalhao}
        salvarMalha={salvarMalha}
        baixarMalha={baixarMalha}
        salvarContorno={salvarContorno}
        openCorrigirKinks={openCorrigirKinks}
        setOpenCorrigirKinks={setOpenCorrigirKinks}
        correcaoSobreposicoes={correcaoSobreposicoes}
        temAlgoParaCorrigir={temAlgoParaCorrigir}
        aplicarCorrecaoSobreposicoes={aplicarCorrecaoSobreposicoes}
        openReducaoTrilhas={openReducaoTrilhas}
        setOpenReducaoTrilhas={setOpenReducaoTrilhas}
        distanciaTrilhas={distanciaTrilhas}
        setDistanciaTrilhas={setDistanciaTrilhas}
        previaReducaoTrilhas={previaReducaoTrilhas}
        aplicarReducaoTrilhas={aplicarReducaoTrilhas}
        openReducaoRetas={openReducaoRetas}
        setOpenReducaoRetas={setOpenReducaoRetas}
        toleranciaRetas={toleranciaRetas}
        setToleranciaRetas={setToleranciaRetas}
        previaReducaoRetas={previaReducaoRetas}
        aplicarReducaoRetas={aplicarReducaoRetas}
      />

      {/* @ts-ignore */}
      <GoogleMap
        onLoad={aoCarregarMapa}
        onClick={aoClicarNoMapa}
        onMouseMove={aoMoverMouseNoMapa}
        mapContainerStyle={{ height: '100%', width: '100%', position: 'relative', overflow: 'hidden' }}
        center={center}
        zoom={area.length > 0 ? 15 : 4}
        options={{
          mapTypeId: 'satellite',
          mapTypeControl: false,
          streetViewControl: false,
          fullscreenControl: false,
          disableDefaultUI: false,
        }}
        onRightClick={() => { }}
      >
        <MalhaGrid
          polygon={polygonKml}
          novoPonto={novoPonto}
          moverPonto={moverPonto}
          removerPonto={removerPonto}
          selecionarPonto={selecionarPonto}
          rotation={rotation}
          isSliding={movendo}
          gridLevel={Number(gridLevel.replace(',', '.'))}
          moverHorizontal={position?.x}
          moverVertical={position?.y}
          tab_selecionado={tab_container_atual}
          abaAtual={abaAtual}
          grid={grid}
          setGrid={setGrid}
          gridOriginal={gridOriginal}
          setGridOriginal={setGridOriginal}
          gridOriginalRotacao={gridOriginalRotacao}
          setGridOriginalRotacao={setGridOriginalRotacao}
          setTamanhoGridOriginal={setTamanhoGridOriginal}
          abrirModal={abrirModal}
          setAbrirModal={setAbrirModal}
          confirmado={confirmado}
          setConfirmado={setConfirmado}
          pontosSelecionados={pontosSelecionados}
          setPontosSelecionados={setPontosSelecionados}
          mousePosition={mousePosition}
          setMousePosition={setMousePosition}
          ancora={ancora}
          mapClickEvent={mapClickEvent}
        />

        {/* Polígono do KML — editável quando em modo edição */}
        <GooglePolygon
          paths={kmlPolygonPaths}
          editable={editandoKml}
          draggable={false}
          onLoad={(polygon) => { polygonRef.current = polygon }}
          onUnmount={() => { polygonRef.current = null }}
          onRightClick={removerVertice}
          onMouseUp={aoEditarPoligono}
          options={{
            fillOpacity: editandoKml ? 0.05 : 0,
            fillColor: editandoKml ? '#ff4444' : undefined,
            strokeColor: editandoKml ? '#ff4444' : kmlComCruzamento ? '#ff4444' : 'cyan',
            strokeWeight: editandoKml ? 3 : 2,
            clickable: editandoKml,
          }}
        />

        {/* Prévia da redução — contorno resultante em verde */}
        <Render condition={previewNoMapa.contorno.length > 0}>
          <GooglePolygon
            paths={previewNoMapa.contorno}
            editable={false}
            draggable={false}
            options={{
              fillOpacity: 0,
              strokeColor: '#00e676',
              strokeWeight: 3,
              clickable: false,
              zIndex: 5,
            }}
          />
        </Render>

        {/* Prévia da redução — vértices que serão removidos */}
        {previewNoMapa.descartados.map((pt, i) => (
          <Marker
            key={`descartado_${i}`}
            position={pt}
            icon={{
              path: window.google.maps.SymbolPath.CIRCLE,
              fillColor: '#ff9100',
              fillOpacity: 0.9,
              strokeColor: '#7a3f00',
              strokeWeight: 1,
              scale: 4,
            }}
            clickable={false}
            zIndex={6}
          />
        ))}

        {/* Marcadores nos pontos de interseção */}
        {pontosKinks.map((pt, i) => (
          <Marker
            key={`kink_${i}`}
            position={pt}
            icon={{
              path: 'M 0,0 C -2,-20 -10,-22 -10,-30 A 10,10 0 1,1 10,-30 C 10,-22 2,-20 0,0 z',
              fillColor: '#ff0000',
              fillOpacity: 1,
              strokeColor: '#000',
              strokeWeight: 1,
              scale: 1,
              labelOrigin: new window.google.maps.Point(0, -28)
            }}
            label={{ text: '!', color: 'white', fontWeight: 'semi-bold' }}
          />
        ))}
      </GoogleMap>

      {/* Overlay de loading ao ajustar contorno */}
      <Render condition={loadingKmlAdjustment}>
        <div style={{
          position: 'absolute',
          top: 0,
          left: 0,
          width: '100%',
          height: '100%',
          backgroundColor: 'rgba(255,255,255,0.7)',
          zIndex: 1000,
          display: 'flex',
          justifyContent: 'center',
          alignItems: 'center',
          flexDirection: 'column'
        }}>
          <Carregando animation legenda='Ajustando contorno...' justifyContent='center' height={100} />
        </div>
      </Render>
    </div>
  )
}

export default GridMap
